import React, { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { T, sans } from './VendorDetails';

/**
 * Security Profile: how the vendor's externally observed posture maps onto a control framework
 * (ISO 27001, NIST CSF, CIS). Read-only and deterministic: GET /vendors/{id}/security-profile rolls up the latest
 * scan's checks per control (routes/security_profile_routes.py). Plain-language statuses:
 *   not_implemented        -> Risks found      (at least one mapped check failed)
 *   partially_implemented  -> No issues found  (every check we could run passed; the process side still needs documents)
 *   fully_implemented      -> Verified         (not reachable from scan evidence today)
 *   no_evidence            -> Needs documents  (nothing a scan can observe)
 */

type ControlStatus = 'fully_implemented' | 'partially_implemented' | 'not_implemented' | 'no_evidence';
type CheckStatus = 'clean' | 'flagged' | 'not_tested';
interface Check {
  indicator_name: string; indicator_category: string; status: CheckStatus;
  severity: string | null; description: string | null; evidence_value: string | null;
}
interface Control { control_id: string; title: string; family: string; category: string | null; status: ControlStatus; checks: Check[] }
interface Family { family: string; controls: Control[] }
interface Profile {
  framework: string; total_controls: number; counts: Record<ControlStatus, number>;
  scan_evaluated: boolean; scan_completed_at: string | null; families: Family[];
}

const SEV_ORDER = ['Critical', 'High', 'Medium', 'Low'];
const SEV_RANK = (s: string | null) => { const i = SEV_ORDER.indexOf(s || ''); return i < 0 ? 9 : i; };

// Functions, not module constants: this file and VendorDetails.tsx import each other (see T).
const statusMeta = (): Record<ControlStatus, { label: string; hint: string; color: string }> => ({
  not_implemented: { label: 'Risks found', hint: 'At least one check failed', color: '#DC2626' },
  partially_implemented: { label: 'No issues found', hint: 'Every check we could run passed', color: '#16A34A' },
  fully_implemented: { label: 'Verified', hint: 'Confirmed with documents', color: '#15803D' },
  no_evidence: { label: 'Needs documents', hint: "Can't be assessed from outside", color: '#94A3B8' },
});
const sevColor = (s: string | null) => ({ Critical: '#B91C1C', High: '#EA580C', Medium: '#D97706', Low: '#2563EB' } as Record<string, string>)[s || ''] || '#64748B';

/** Scan text carries emoji and mis-decoded characters; show plain text only. */
const plain = (v: unknown) => String(v ?? '')
  .replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, '-')
  .replace(/[^\x20-\x7E\n]/g, '').replace(/[ \t]{2,}/g, ' ').trim();

const STATUS_FILTERS: (ControlStatus | 'all')[] = ['all', 'not_implemented', 'partially_implemented', 'no_evidence'];

export default function VendorSecurityProfile({ vendorId, isDark, onOpenTab }: {
  vendorId: string; isDark: boolean; onOpenTab?: (tab: string) => void;
}) {
  const c = {
    ink: isDark ? '#e5e7eb' : T.ink, body: isDark ? '#cbd5e1' : '#334155', slate: isDark ? '#94a3b8' : T.slate,
    muted: isDark ? '#64748b' : T.muted, line: isDark ? '#1f1f1f' : T.bdr, soft: isDark ? '#181818' : '#EEF1F5',
    surface: isDark ? '#0d0d0d' : T.surface, surface2: isDark ? '#141414' : T.surface2, accent: isDark ? '#3B82F6' : '#2563EB',
    accentBg: isDark ? '#0b1730' : T.blueBg,
  };
  const card: React.CSSProperties = { background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12 };
  const eyebrow: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted };
  const tint = (hex: string) => `${hex}${isDark ? '26' : '14'}`;
  const meta = statusMeta();

  const [framework, setFramework] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ControlStatus | 'all'>('all');
  const [familyFilter, setFamilyFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openControl, setOpenControl] = useState<Control | null>(null);
  const [showAllGaps, setShowAllGaps] = useState(false);
  const [howOpen, setHowOpen] = useState(false);

  const frameworksQuery = useQuery({
    queryKey: ['vendors', 'detail', vendorId, 'security-profile', 'frameworks'],
    queryFn: async () => ((await api.get(`/v1/vendors/${vendorId}/security-profile/frameworks`)).data.frameworks || []) as { id: string; name: string; total_controls: number }[],
  });
  const profileQuery = useQuery({
    queryKey: ['vendors', 'detail', vendorId, 'security-profile', 'profile', framework],
    queryFn: async () => {
      const qs = framework ? `?framework=${encodeURIComponent(framework)}` : '';
      return (await api.get(`/v1/vendors/${vendorId}/security-profile${qs}`)).data as Profile;
    },
    placeholderData: keepPreviousData,
  });
  const profile = profileQuery.data ?? null;
  const switching = profileQuery.isPlaceholderData;
  // Lay out by the tab's own width (the vendor sidebar takes part of the window).
  const rootRef = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(1000);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [profileQuery.isPending]);
  const narrow = w < 760;

  const switchFramework = (name: string) => {
    setFramework(name); setStatusFilter('all'); setFamilyFilter(null); setSearch(''); setOpenControl(null); setShowAllGaps(false);
  };

  useEffect(() => {
    if (!openControl && !howOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpenControl(null); setHowOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openControl, howOpen]);

  const controls = useMemo(() => profile ? profile.families.flatMap(f => f.controls) : [], [profile]);

  // Unique checks across the framework (one check can support several controls).
  const checkSummary = useMemo(() => {
    const seen = new Map<string, Check>();
    controls.forEach(ct => ct.checks.forEach(ch => { if (!seen.has(ch.indicator_name)) seen.set(ch.indicator_name, ch); }));
    const all = Array.from(seen.values());
    const failing = all.filter(ch => ch.status === 'flagged');
    const bySev: Record<string, number> = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    failing.forEach(ch => { if (ch.severity && ch.severity in bySev) bySev[ch.severity]++; });
    return { total: all.length, failing: failing.length, passing: all.filter(ch => ch.status === 'clean').length, untested: all.filter(ch => ch.status === 'not_tested').length, bySev };
  }, [controls]);

  const gaps = useMemo(() => controls
    .filter(ct => ct.status === 'not_implemented')
    .map(ct => ({ ct, failing: ct.checks.filter(ch => ch.status === 'flagged').sort((a, b) => SEV_RANK(a.severity) - SEV_RANK(b.severity)) }))
    .sort((a, b) => (SEV_RANK(a.failing[0]?.severity) - SEV_RANK(b.failing[0]?.severity)) || (b.failing.length - a.failing.length)),
  [controls]);

  const familyStats = useMemo(() => (profile?.families ?? []).map(f => {
    const n = (s: ControlStatus) => f.controls.filter(ct => ct.status === s).length;
    return { family: f.family, total: f.controls.length, risks: n('not_implemented'), clean: n('partially_implemented') + n('fully_implemented'), docs: n('no_evidence') };
  }), [profile]);

  const q = search.trim().toLowerCase();
  const visibleFamilies = (profile?.families ?? [])
    .filter(f => !familyFilter || f.family === familyFilter)
    .map(f => ({
      ...f,
      controls: f.controls.filter(ct => {
        if (statusFilter !== 'all' && ct.status !== statusFilter && !(statusFilter === 'partially_implemented' && ct.status === 'fully_implemented')) return false;
        if (q && !`${ct.control_id} ${ct.title} ${ct.category || ''} ${ct.checks.map(ch => ch.indicator_name).join(' ')}`.toLowerCase().includes(q)) return false;
        return true;
      }),
    }))
    .filter(f => f.controls.length > 0);
  const filtersOn = statusFilter !== 'all' || !!familyFilter || !!q;

  /* ── loading / error ── */
  if (profileQuery.isPending) {
    return (
      <div ref={rootRef} style={{ fontFamily: sans }}>
        <style>{'@keyframes spShimmer { 0%,100% { opacity: .5 } 50% { opacity: 1 } }'}</style>
        {[70, 190, 220, 320].map((h, i) => <div key={i} style={{ ...card, height: h, marginBottom: 14, animation: 'spShimmer 1.2s infinite' }} />)}
      </div>
    );
  }
  if (!profile) {
    return (
      <div style={{ ...card, padding: 36, textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.ink }}>Couldn't load the Security Profile</div>
        <button onClick={() => profileQuery.refetch()} style={btn(c, false)}>Try again</button>
      </div>
    );
  }

  const { counts, total_controls } = profile;
  const risks = counts.not_implemented, clean = counts.partially_implemented + counts.fully_implemented, docs = counts.no_evidence;
  const assessable = risks + clean;
  const frameworks = frameworksQuery.data?.length ? frameworksQuery.data : [{ id: 'current', name: profile.framework, total_controls }];
  const scanDate = profile.scan_completed_at ? new Date(profile.scan_completed_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  const segments: { key: ControlStatus; n: number }[] = [
    { key: 'not_implemented', n: risks }, { key: 'partially_implemented', n: clean }, { key: 'no_evidence', n: docs },
  ];

  return (
    <div ref={rootRef} style={{ fontFamily: sans, color: c.ink, opacity: switching ? 0.55 : 1, transition: 'opacity .15s' }}>
      <style>{'@keyframes spSlide { from { transform: translateX(16px); opacity: 0 } to { transform: none; opacity: 1 } } @keyframes spPop { from { transform: translateY(6px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } } .sp-row:hover { background: var(--sp-hover) } .sp-gap:hover { border-color: var(--sp-hl) !important }'}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Security Profile</div>
          <div style={{ fontSize: 13.5, color: c.slate, marginTop: 4, maxWidth: 680, lineHeight: 1.5 }}>
            How this vendor's externally visible security lines up with a control framework.
            {profile.scan_evaluated ? <> Based on the scan of {scanDate}.</> : <> No completed scan yet, so nothing can be assessed.</>}
            {' '}<button onClick={() => setHowOpen(true)} style={linkBtn(c)}>How this works</button>
          </div>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={eyebrow}>Framework</span>
          <select value={profile.framework} onChange={e => switchFramework(e.target.value)} disabled={switching} style={{
            height: 36, minWidth: 230, padding: '0 10px', borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface,
            color: c.ink, fontFamily: sans, fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer',
          }}>
            {frameworks.map(f => <option key={f.id} value={f.name}>{f.name} ({f.total_controls} controls)</option>)}
          </select>
        </label>
      </div>

      {/* ── Overview ── */}
      <div style={{ ...card, display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1fr) minmax(240px, 300px)', marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ padding: '20px 22px' }}>
          <div style={eyebrow}>{profile.framework} · {total_controls} controls</div>
          <div style={{ fontSize: 17, fontWeight: 650, marginTop: 8, lineHeight: 1.45, maxWidth: 640 }}>
            {!profile.scan_evaluated ? 'Run a scan to see which controls this vendor meets.' : (
              <>
                <span style={{ color: risks ? meta.not_implemented.color : c.ink }}>{risks} control{risks === 1 ? ' shows' : 's show'} risks</span>
                {', '}
                <span style={{ color: meta.partially_implemented.color }}>{clean} {clean === 1 ? 'looks' : 'look'} healthy</span>
                {' from the outside, and '}
                <span style={{ color: c.slate }}>{docs} can only be confirmed with documents</span>.
              </>
            )}
          </div>
          <div style={{ display: 'flex', height: 12, borderRadius: 99, overflow: 'hidden', background: c.soft, marginTop: 16, gap: 2 }}>
            {segments.filter(s => s.n > 0).map(s => (
              <button key={s.key} title={`${meta[s.key].label}: ${s.n}`} onClick={() => setStatusFilter(statusFilter === s.key ? 'all' : s.key)}
                style={{ width: `${(s.n / total_controls) * 100}%`, background: s.key === 'no_evidence' ? (isDark ? '#334155' : '#CBD5E1') : meta[s.key].color, border: 'none', padding: 0, cursor: 'pointer' }} />
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: w < 520 ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 16 }}>
            {segments.map(s => {
              const on = statusFilter === s.key;
              return (
                <button key={s.key} onClick={() => setStatusFilter(on ? 'all' : s.key)} style={{
                  textAlign: 'left', padding: '11px 13px', borderRadius: 10, cursor: 'pointer', fontFamily: sans,
                  border: `1px solid ${on ? meta[s.key].color : c.line}`, background: on ? tint(meta[s.key].color) : c.surface,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: meta[s.key].color }} />
                    <span style={{ fontSize: 12.5, fontWeight: 650, color: c.ink }}>{meta[s.key].label}</span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4, color: s.n && s.key === 'not_implemented' ? meta[s.key].color : c.ink }}>{s.n}</div>
                  <div style={{ fontSize: 11.5, color: c.muted }}>{meta[s.key].hint}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ padding: '20px 22px', borderLeft: narrow ? 'none' : `1px solid ${c.line}`, borderTop: narrow ? `1px solid ${c.line}` : 'none', background: c.surface2, display: 'flex', flexDirection: 'column' }}>
          <div style={eyebrow}>Checks behind these controls</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 800, color: checkSummary.failing ? '#DC2626' : c.ink }}>{checkSummary.failing}</span>
            <span style={{ fontSize: 13, color: c.slate }}>failing of {checkSummary.total}</span>
          </div>
          <div style={{ display: 'grid', gap: 7, marginTop: 12 }}>
            {SEV_ORDER.map(sv => {
              const n = checkSummary.bySev[sv];
              const max = Math.max(1, ...Object.values(checkSummary.bySev));
              return (
                <div key={sv} style={{ display: 'grid', gridTemplateColumns: '62px 1fr 22px', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: c.slate }}>{sv}</span>
                  <span style={{ height: 6, borderRadius: 99, background: c.soft, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${(n / max) * 100}%`, background: sevColor(sv), borderRadius: 99 }} />
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', color: n ? sevColor(sv) : c.muted }}>{n}</span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 'auto', paddingTop: 14, fontSize: 12, color: c.slate, lineHeight: 1.6 }}>
            <b style={{ color: '#16A34A' }}>{checkSummary.passing}</b> passing · <b style={{ color: c.ink }}>{checkSummary.untested}</b> couldn't be tested
            <div style={{ color: c.muted }}>{assessable} of {total_controls} controls can be assessed by scanning.</div>
          </div>
        </div>
      </div>

      {/* ── Where the risks are ── */}
      {gaps.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Where the risks are</div>
              <div style={{ fontSize: 12.5, color: c.muted, marginTop: 2 }}>Controls with at least one failing check, most severe first.</div>
            </div>
            {gaps.length > 6 && <button onClick={() => setShowAllGaps(v => !v)} style={linkBtn(c)}>{showAllGaps ? 'Show fewer' : `Show all ${gaps.length}`}</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${narrow ? 260 : 330}px, 1fr))`, gap: 10, ['--sp-hl' as any]: c.accent }}>
            {(showAllGaps ? gaps : gaps.slice(0, 6)).map(({ ct, failing }) => (
              <button key={ct.control_id} className="sp-gap" onClick={() => setOpenControl(ct)} style={{
                ...card, textAlign: 'left', padding: '14px 16px', cursor: 'pointer', fontFamily: sans, display: 'flex', flexDirection: 'column', gap: 10,
                transition: 'border-color .12s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <ControlId id={ct.control_id} c={c} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 650, color: c.ink, lineHeight: 1.35 }}>{ct.title}</div>
                    <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>{ct.family}</div>
                  </div>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#DC2626', whiteSpace: 'nowrap' }}>{failing.length} failing</span>
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {failing.slice(0, 3).map(ch => (
                    <div key={ch.indicator_name} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <SevChip sev={ch.severity} tint={tint} />
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: c.body, whiteSpace: 'nowrap' }}>{ch.indicator_name}</span>
                      {ch.evidence_value && <span style={{ fontSize: 12, color: c.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{plain(ch.evidence_value)}</span>}
                    </div>
                  ))}
                  {failing.length > 3 && <span style={{ fontSize: 12, color: c.muted }}>+{failing.length - 3} more</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Coverage by area ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Coverage by area</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
          {familyStats.map(f => {
            const on = familyFilter === f.family;
            return (
              <button key={f.family} onClick={() => setFamilyFilter(on ? null : f.family)} style={{
                ...card, textAlign: 'left', padding: '13px 14px', cursor: 'pointer', fontFamily: sans,
                borderColor: on ? c.accent : c.line, boxShadow: on ? `0 0 0 1px ${c.accent}` : 'none',
              }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: c.ink, lineHeight: 1.35, minHeight: 35 }}>{f.family}</div>
                <div style={{ display: 'flex', height: 6, borderRadius: 99, overflow: 'hidden', background: c.soft, gap: 1, marginTop: 8 }}>
                  {f.risks > 0 && <span style={{ width: `${(f.risks / f.total) * 100}%`, background: '#DC2626' }} />}
                  {f.clean > 0 && <span style={{ width: `${(f.clean / f.total) * 100}%`, background: '#16A34A' }} />}
                  {f.docs > 0 && <span style={{ width: `${(f.docs / f.total) * 100}%`, background: isDark ? '#334155' : '#CBD5E1' }} />}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8, fontSize: 11.5, color: c.slate, flexWrap: 'wrap' }}>
                  <span>{f.total} controls</span>
                  {f.risks > 0 && <span style={{ color: '#DC2626', fontWeight: 650 }}>{f.risks} risks</span>}
                  {f.clean > 0 && <span style={{ color: '#16A34A', fontWeight: 650 }}>{f.clean} healthy</span>}
                  {f.risks + f.clean === 0 && <span style={{ color: c.muted }}>documents only</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── All controls ── */}
      <div style={{ ...card, overflow: 'hidden', ['--sp-hover' as any]: c.surface2 }}>
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginRight: 6 }}>All controls</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(s => {
              const on = statusFilter === s;
              const n = s === 'all' ? total_controls : s === 'partially_implemented' ? clean : counts[s];
              return (
                <button key={s} onClick={() => setStatusFilter(s)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                  border: `1px solid ${on ? c.ink : c.line}`, background: on ? c.ink : c.surface, color: on ? (isDark ? '#000' : '#fff') : c.slate,
                }}>
                  {s !== 'all' && <span style={{ width: 7, height: 7, borderRadius: 99, background: meta[s].color }} />}
                  {s === 'all' ? 'All' : meta[s].label}<span style={{ opacity: 0.7, fontSize: 11 }}>{n}</span>
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1 }} />
          {familyFilter && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 7, background: c.accentBg, color: c.accent, fontWeight: 600 }}>
              {familyFilter}<button onClick={() => setFamilyFilter(null)} aria-label="Clear area filter" style={{ border: 'none', background: 'none', color: c.accent, cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
            </span>
          )}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search controls or checks" style={{
            width: 240, maxWidth: '100%', height: 32, padding: '0 10px', borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface2,
            color: c.ink, fontFamily: sans, fontSize: 12.5, outline: 'none',
          }} />
        </div>

        {visibleFamilies.length === 0 ? (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: c.muted, fontSize: 13 }}>
            No controls match. {filtersOn && <button onClick={() => { setStatusFilter('all'); setFamilyFilter(null); setSearch(''); }} style={linkBtn(c)}>Clear filters</button>}
          </div>
        ) : visibleFamilies.map(f => (
          <div key={f.family}>
            <div style={{ padding: '8px 14px', background: c.surface2, borderBottom: `1px solid ${c.line}`, fontSize: 11.5, fontWeight: 700, letterSpacing: '.04em', color: c.slate, display: 'flex', justifyContent: 'space-between' }}>
              <span>{f.family.toUpperCase()}</span><span style={{ fontWeight: 600 }}>{f.controls.length}</span>
            </div>
            {f.controls.map(ct => {
              const m = meta[ct.status];
              const pass = ct.checks.filter(ch => ch.status === 'clean').length;
              const fail = ct.checks.filter(ch => ch.status === 'flagged').length;
              const tested = pass + fail;
              const statusChip = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 650, color: ct.status === 'no_evidence' ? c.slate : m.color, padding: '3px 9px', borderRadius: 99, background: tint(m.color), justifySelf: 'start', whiteSpace: 'nowrap' }}>
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: m.color }} />{m.label}
                </span>
              );
              return (
                <div key={ct.control_id} className="sp-row" role="button" tabIndex={0} onClick={() => setOpenControl(ct)}
                  onKeyDown={e => { if (e.key === 'Enter') setOpenControl(ct); }}
                  style={{
                    display: 'grid', alignItems: 'center', gap: narrow ? '6px 10px' : 12, padding: '10px 14px', borderBottom: `1px solid ${c.soft}`, cursor: 'pointer',
                    gridTemplateColumns: narrow ? '58px minmax(0, 1fr) 14px' : '64px minmax(0, 1fr) 150px 150px 18px',
                  }}>
                  <ControlId id={ct.control_id} c={c} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: ct.status === 'no_evidence' ? c.slate : c.ink, lineHeight: 1.35 }}>{ct.title}</div>
                    {ct.category && <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>{ct.category}</div>}
                  </div>
                  {narrow && <span style={{ color: c.muted, fontSize: 14, gridRow: '1 / span 2', gridColumn: 3 }}>›</span>}
                  <div style={narrow ? { gridColumn: '2 / 3', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } : undefined}>
                    {tested > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ display: 'flex', width: 60, height: 5, borderRadius: 99, overflow: 'hidden', background: c.soft }}>
                          <span style={{ width: `${(pass / tested) * 100}%`, background: '#16A34A' }} />
                          <span style={{ width: `${(fail / tested) * 100}%`, background: '#DC2626' }} />
                        </span>
                        <span style={{ fontSize: 12, color: fail ? '#DC2626' : c.slate, fontWeight: fail ? 650 : 500 }}>
                          {fail ? `${fail} failing` : `${pass} passing`}
                        </span>
                      </div>
                    ) : <span style={{ fontSize: 12, color: c.muted }}>{ct.checks.length ? "Couldn't test" : 'No scan checks'}</span>}
                    {narrow && statusChip}
                  </div>
                  {!narrow && statusChip}
                  {!narrow && <span style={{ color: c.muted, fontSize: 14 }}>›</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Control drawer ── */}
      {openControl && (() => {
        const ct = openControl, m = meta[ct.status];
        const groups: { key: CheckStatus; title: string; color: string }[] = [
          { key: 'flagged', title: 'Failing', color: '#DC2626' }, { key: 'clean', title: 'Passing', color: '#16A34A' }, { key: 'not_tested', title: "Couldn't test", color: c.muted },
        ];
        const failCount = ct.checks.filter(ch => ch.status === 'flagged').length;
        const passCount = ct.checks.filter(ch => ch.status === 'clean').length;
        const verdict = ct.status === 'not_implemented'
          ? `${failCount} of ${ct.checks.length} check${ct.checks.length === 1 ? '' : 's'} failed. Fixing ${failCount === 1 ? 'it' : 'them'} moves this control to "No issues found".`
          : ct.status === 'no_evidence'
            ? ct.checks.length ? 'None of the mapped checks could be tested on the latest scan.' : 'This control is about policies, people or physical security, which a scan cannot see.'
            : `All ${passCount} check${passCount === 1 ? '' : 's'} we could run passed. The documented process behind this control still needs evidence from the vendor.`;
        return (
          <>
            <div onClick={() => setOpenControl(null)} style={{ position: 'fixed', top: 64, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,.3)', zIndex: 1200 }} />
            <aside role="dialog" aria-label={`Control ${ct.control_id}`} style={{
              position: 'fixed', top: 64, right: 0, bottom: 0, width: 540, maxWidth: '100%', zIndex: 1201, display: 'flex', flexDirection: 'column',
              background: c.surface, borderLeft: `1px solid ${c.line}`, fontFamily: sans, animation: 'spSlide .18s ease-out',
            }}>
              <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${c.line}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <ControlId id={ct.control_id} c={c} />
                    <span style={{ fontSize: 12, color: c.muted }}>{ct.family}{ct.category ? ` · ${ct.category}` : ''}</span>
                  </div>
                  <button onClick={() => setOpenControl(null)} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface2, color: c.slate, cursor: 'pointer', flexShrink: 0 }}>×</button>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c.ink, marginTop: 10, lineHeight: 1.35 }}>{ct.title}</div>
                <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: tint(m.color), display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: m.color, marginTop: 6, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: ct.status === 'no_evidence' ? c.ink : m.color }}>{m.label}</div>
                    <div style={{ fontSize: 12.5, color: c.body, marginTop: 2, lineHeight: 1.5 }}>{verdict}</div>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 24px' }}>
                {ct.checks.length === 0 ? (
                  <div style={{ fontSize: 13, color: c.slate, lineHeight: 1.6 }}>No external scan check maps to this control.</div>
                ) : groups.map(g => {
                  const list = ct.checks.filter(ch => ch.status === g.key).sort((a, b) => SEV_RANK(a.severity) - SEV_RANK(b.severity));
                  if (!list.length) return null;
                  return (
                    <div key={g.key} style={{ marginBottom: 16 }}>
                      <div style={{ ...eyebrow, color: g.color, marginBottom: 8 }}>{g.title} · {list.length}</div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {list.map(ch => <CheckCard key={ch.indicator_name} ch={ch} c={c} tint={tint} />)}
                      </div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 8, padding: '12px 14px', borderRadius: 10, border: `1px dashed ${c.line}`, fontSize: 12.5, color: c.slate, lineHeight: 1.55 }}>
                  Scans show the technical side of a control. To confirm the process behind it, ask the vendor for a policy, certificate or audit report.
                  {onOpenTab && <div style={{ marginTop: 8 }}><button onClick={() => { setOpenControl(null); onOpenTab('documents'); }} style={btn(c, false)}>Open Documents</button></div>}
                </div>
              </div>
            </aside>
          </>
        );
      })()}

      {/* ── How this works ── */}
      {howOpen && (
        <div onMouseDown={e => { if (e.target === e.currentTarget) setHowOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 560, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, fontFamily: sans, animation: 'spPop .16s ease-out', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px 6px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.ink }}>How the Security Profile works</div>
              <button onClick={() => setHowOpen(false)} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface2, color: c.slate, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '6px 24px 22px', fontSize: 13.5, color: c.body, lineHeight: 1.6 }}>
              <p style={{ margin: '0 0 12px' }}>Each control in the framework is linked to the scan checks that can speak to it. We look at the vendor's latest scan and give every control one status:</p>
              {(['not_implemented', 'partially_implemented', 'no_evidence'] as ControlStatus[]).map(s => (
                <div key={s} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 99, background: meta[s].color, marginTop: 7, flexShrink: 0 }} />
                  <div><b style={{ color: c.ink }}>{meta[s].label}</b> - {{
                    not_implemented: 'at least one linked check failed. Any failure counts, however many others pass.',
                    partially_implemented: 'every linked check we could run passed. It is not "fully verified": the documented process behind the control can only be confirmed with documents.',
                    fully_implemented: '',
                    no_evidence: 'no scan check can observe this control (policies, people, physical security), or none could be tested.',
                  }[s]}</div>
                </div>
              ))}
              <p style={{ margin: '12px 0 0', color: c.slate }}>Checks that could not run (for example, nothing to test) are shown but never counted as a pass. Nothing here changes the vendor's risk score.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ControlId({ id, c }: { id: string; c: Record<string, string> }) {
  return (
    <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11.5, fontWeight: 600, color: c.slate, background: c.surface2, border: `1px solid ${c.line}`, padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap', justifySelf: 'start' }}>{id}</span>
  );
}

function SevChip({ sev, tint }: { sev: string | null; tint: (h: string) => string }) {
  const col = sevColor(sev);
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: col, background: tint(col), padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap', flexShrink: 0 }}>{sev || 'Finding'}</span>;
}

function CheckCard({ ch, c, tint }: { ch: Check; c: Record<string, string>; tint: (h: string) => string }) {
  const [more, setMore] = useState(false);
  const desc = plain(ch.description);
  return (
    <div style={{ padding: '11px 13px', borderRadius: 10, border: `1px solid ${c.line}`, background: c.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {ch.status === 'flagged' ? <SevChip sev={ch.severity} tint={tint} />
          : <span style={{ fontSize: 10.5, fontWeight: 700, color: ch.status === 'clean' ? '#16A34A' : c.muted, background: tint(ch.status === 'clean' ? '#16A34A' : '#94A3B8'), padding: '2px 7px', borderRadius: 5 }}>{ch.status === 'clean' ? 'Pass' : 'Not tested'}</span>}
        <span style={{ fontSize: 13, fontWeight: 650, color: c.ink }}>{ch.indicator_name}</span>
        <span style={{ fontSize: 11.5, color: c.muted, marginLeft: 'auto' }}>{ch.indicator_category}</span>
      </div>
      <div style={{ fontSize: 12.5, color: c.body, marginTop: 6, wordBreak: 'break-word' }}>
        {ch.status === 'not_tested' ? 'The scan had nothing to evaluate for this check.' : ch.evidence_value ? plain(ch.evidence_value) : 'No details recorded.'}
      </div>
      {desc && (
        <div style={{ fontSize: 12, color: c.slate, marginTop: 6, lineHeight: 1.5 }}>
          {more || desc.length <= 160 ? desc : `${desc.slice(0, 160)}...`}
          {desc.length > 160 && <button onClick={() => setMore(v => !v)} style={{ ...linkBtn(c), fontSize: 12, marginLeft: 4 }}>{more ? 'Less' : 'More'}</button>}
        </div>
      )}
    </div>
  );
}

const linkBtn = (c: Record<string, string>): React.CSSProperties => ({ background: 'none', border: 'none', padding: 0, color: c.accent, cursor: 'pointer', fontFamily: sans, fontSize: 13, fontWeight: 600 });
const btn = (c: Record<string, string>, primary: boolean): React.CSSProperties => ({
  marginTop: 0, padding: '7px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
  border: primary ? 'none' : `1px solid ${c.line}`, background: primary ? c.accent : c.surface, color: primary ? '#fff' : c.ink,
});
